// Helper for MediaSession API
// This allows Android/iOS lock screen controls to work and sync with our app state.

interface MediaMetadataParams {
    title: string;
    artist?: string;
    album?: string;
    artwork?: { src: string; sizes?: string; type?: string }[];
}

export const updateMediaSession = (state: 'playing' | 'paused' | 'stopped', metadata?: MediaMetadataParams, handlers?: {
    onPlay?: () => void;
    onPause?: () => void;
    onStop?: () => void;
    onNext?: () => void;
    onPrev?: () => void;
}) => {
    if (!('mediaSession' in navigator)) return;

    // 1. Update State
    // Note: 'stopped' isn't a valid playbackState in all browsers, usually 'none' or 'paused'.
    // Chrome supports: 'playing', 'paused', 'none'.
    const playbackState = state === 'playing' ? 'playing' : 'paused'; // treat stopped as paused for widget
    navigator.mediaSession.playbackState = playbackState;

    // 2. Update Metadata if provided
    if (metadata) {
        navigator.mediaSession.metadata = new MediaMetadata({
            title: metadata.title,
            artist: metadata.artist || 'ClaraNarratio',
            album: metadata.album || 'claranarratio.com',
            artwork: metadata.artwork || [
                { src: '/icons/icon-192.png', sizes: '192x192', type: 'image/png' },
                { src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' }
            ]
        });
    }

    // 3. Register Handlers
    if (handlers) {
        const { onPlay, onPause, onStop, onNext, onPrev } = handlers;

        if (onPlay) navigator.mediaSession.setActionHandler('play', onPlay);
        if (onPause) navigator.mediaSession.setActionHandler('pause', onPause);
        if (onStop) navigator.mediaSession.setActionHandler('stop', onStop);

        // Only set Next/Prev if provided (might want to disable them if no next chapter)
        if (onNext) navigator.mediaSession.setActionHandler('nexttrack', onNext);
        else navigator.mediaSession.setActionHandler('nexttrack', null);

        if (onPrev) navigator.mediaSession.setActionHandler('previoustrack', onPrev);
        else navigator.mediaSession.setActionHandler('previoustrack', null);
    }
};
